import { NotificationResponse } from 'api/actor';
import { FileTypeEnum, SimulationDocumentDto } from 'api/documents';
import { useCallback } from 'react';
import { StepDocumentNotificationStepEnum } from '../types/StepDocumentNotificationStepEnum';
import {
  useSignalRNotifications,
  DOCUMENT_NOTIFICATIONS_CONFIG
} from './useSignalRNotifications';

export interface DocumentProgressDto extends SimulationDocumentDto {
  step: StepDocumentNotificationStepEnum;
  replaceFileType?: FileTypeEnum;
  notificationPopIn: NotificationResponse;
}

/**
 * Hook pour les notifications de documents via SignalR
 *
 * @param quoteId - ID du devis pour rejoindre le groupe de notifications
 *
 * @example
 * ```tsx
 * const { notifications, isConnected, memoizedConnect, memoizedDisconnect } =
 *   useDocumentNotifications(quoteId);
 *
 * useEffect(() => {
 *   memoizedConnect();
 *   return memoizedDisconnect;
 * }, [memoizedConnect, memoizedDisconnect]);
 * ```
 */
export const useDocumentNotifications = (quoteId?: string) => {
  const {
    connection,
    isConnected,
    error,
    memoizedConnect,
    memoizedDisconnect,
    notifications,
    latestNotification,
    clearNotifications
  } = useSignalRNotifications<DocumentProgressDto>({
    ...DOCUMENT_NOTIFICATIONS_CONFIG,
    groupId: quoteId
  });

  const getDocumentsByFileType = useCallback(
    (fileType: FileTypeEnum) => {
      return notifications.filter(n => n.fileType === fileType);
    },
    [notifications]
  );

  return {
    // Connection state
    connection,
    isConnected,
    error,

    // Connection methods
    memoizedConnect,
    memoizedDisconnect,

    // Notification data
    notifications,
    latestNotification,

    // Utility methods
    clearNotifications,
    getDocumentsByFileType
  };
};
